import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getPendingOrdersCount } from '../services/api';
import { API_URL } from '../constants/config';
import './BottomNav.css';

interface BottomNavProps {
  isAdmin: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: string;
  activeIcon: string;
  path: string;
  badge?: number;
}

const BottomNav: React.FC<BottomNavProps> = ({ isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { totalItems } = useCart();
  const { isAuthenticated } = useAuth();

  const [pendingOrdersCount, setPendingOrdersCount] =
    useState<number>(0);

  const [userPendingOrdersCount, setUserPendingOrdersCount] =
    useState<number>(0);

  // =========================================================
  // BOTTOM NAV SCROLL VISIBILITY
  //
  // true  = BottomNav is visible
  // false = BottomNav is hidden
  // =========================================================
  const [isNavVisible, setIsNavVisible] = useState<boolean>(true);

  // =========================================================
  // DYNAMIC SHOW / HIDE ON WINDOW SCROLL
  //
  // Scroll DOWN = hide BottomNav
  // Scroll UP   = show BottomNav
  // At TOP      = show BottomNav
  //
  // App.css confirms that .main-content has
  // overflow-y: hidden, so the page scroll is handled
  // by the window/document rather than .main-content.
  // =========================================================
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const SCROLL_THRESHOLD = 12;
    const TOP_THRESHOLD = 10;

    const updateNavVisibility = () => {
      const currentScrollY = window.scrollY;

      // Always show BottomNav when at or very close to the top.
      if (currentScrollY <= TOP_THRESHOLD) {
        setIsNavVisible(true);
        lastScrollY = currentScrollY;
        ticking = false;
        return;
      }

      const scrollDifference =
        currentScrollY - lastScrollY;

      // Ignore very small movements.
      if (Math.abs(scrollDifference) < SCROLL_THRESHOLD) {
        ticking = false;
        return;
      }

      // Scrolling DOWN
      if (scrollDifference > 0) {
        setIsNavVisible(false);
      }

      // Scrolling UP
      else if (scrollDifference < 0) {
        setIsNavVisible(true);
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(
          updateNavVisibility
        );

        ticking = true;
      }
    };

    window.addEventListener(
      'scroll',
      handleScroll,
      { passive: true }
    );

    // Make sure the nav starts visible.
    setIsNavVisible(true);

    return () => {
      window.removeEventListener(
        'scroll',
        handleScroll
      );
    };
  }, []);


  // =========================================================
  // SHOW BOTTOM NAV AGAIN WHEN NAVIGATING TO A NEW PAGE
  // =========================================================
  useEffect(() => {
    setIsNavVisible(true);

    // Reset the scroll tracking position after
    // route changes.
    window.requestAnimationFrame(() => {
      // Nothing else is required here.
      // The scroll listener will continue tracking
      // from the new page position.
    });
  }, [location.pathname]);


  // =========================================================
  // FETCH PENDING ORDERS COUNT FOR ADMIN
  //
  // Only authenticated admins should make this request.
  // =========================================================
  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      setPendingOrdersCount(0);
      return;
    }

    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem('token');

        if (!token) {
          setPendingOrdersCount(0);
          return;
        }

        const response =
          await getPendingOrdersCount();

        if (response.data.success) {
          setPendingOrdersCount(
            response.data.count
          );
        }
      } catch (error) {
        console.error(
          'Failed to fetch pending orders count:',
          error
        );
      }
    };

    fetchPendingCount();

    // Poll every 30 seconds.
    const interval = setInterval(
      fetchPendingCount,
      30000
    );

    return () => clearInterval(interval);
  }, [isAuthenticated, isAdmin]);


  // =========================================================
  // FETCH USER'S PENDING ORDERS COUNT
  //
  // Guests should NEVER make this request.
  // =========================================================
  useEffect(() => {
    if (!isAuthenticated) {
      setUserPendingOrdersCount(0);
      return;
    }

    const fetchUserPendingOrders = async () => {
      try {
        const token = localStorage.getItem('token');

        if (!token) {
          setUserPendingOrdersCount(0);
          return;
        }

        const response = await fetch(
          `${API_URL}/orders/my-orders`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          setUserPendingOrdersCount(0);
          return;
        }

        const data = await response.json();

        if (data.success && data.orders) {
          const pendingCount =
            data.orders.filter(
              (order: any) =>
                order.status === 'pending' ||
                order.status === 'paid' ||
                (
                  order.isPartPayment &&
                  order.partPaymentStatus ===
                    'partial' &&
                  !order.depositConfirmed
                )
            ).length;

          setUserPendingOrdersCount(
            pendingCount
          );
        } else {
          setUserPendingOrdersCount(0);
        }
      } catch (error) {
        console.error(
          'Failed to fetch user pending orders:',
          error
        );
      }
    };

    fetchUserPendingOrders();

    // Refresh every 30 seconds.
    const interval = setInterval(
      fetchUserPendingOrders,
      30000
    );

    return () => clearInterval(interval);
  }, [isAuthenticated]);


  // =========================================================
  // NAVIGATION ITEMS
  //
  // HOME + CART = everyone
  // ORDERS       = authenticated users
  // ADMIN        = authenticated admins
  // =========================================================
  const navItems: NavItem[] = [
    {
      id: 'home',
      label: 'Home',
      icon: '🏠',
      activeIcon: '🏠',
      path: '/'
    },

    {
      id: 'cart',
      label: 'Cart',
      icon: '🛒',
      activeIcon: '🛒',
      path: '/cart',
      badge: totalItems
    }
  ];


  // =========================================================
  // AUTHENTICATED CUSTOMER NAVIGATION
  // =========================================================
  if (isAuthenticated) {
    navItems.push({
      id: 'orders',
      label: 'Orders',
      icon: '📦',
      activeIcon: '📦',
      path: '/my-orders',
      badge: userPendingOrdersCount
    });
  }


  // =========================================================
  // ADMIN NAVIGATION
  // =========================================================
  if (isAuthenticated && isAdmin) {
    navItems.push({
      id: 'admin',
      label: 'Admin',
      icon: '⚙️',
      activeIcon: '⚙️',
      path: '/admin',
      badge: pendingOrdersCount
    });
  }


  // =========================================================
  // HANDLE NAVIGATION
  // =========================================================
  const handleNavClick = (item: NavItem) => {
    // Show the navigation immediately when
    // the user taps a navigation item.
    setIsNavVisible(true);

    navigate(item.path);
  };


  // =========================================================
  // ACTIVE NAVIGATION ITEM
  // =========================================================
  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }

    return location.pathname.startsWith(path);
  };


  // =========================================================
  // DON'T SHOW BOTTOM NAV ON AUTH / SPECIAL PAGES
  //
  // IMPORTANT:
  // We DO NOT hide it on /products or /product/:id.
  // Guests need the Cart button while shopping.
  // =========================================================
  const hideBottomNav =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/ai-chat' ||
    location.pathname.startsWith('/track-order/');

  if (hideBottomNav) {
    return null;
  }


  // =========================================================
  // RENDER
  // =========================================================
  return (
    <nav
      className={`bottom-nav ${
        isNavVisible
          ? 'bottom-nav-visible'
          : 'bottom-nav-hidden'
      }`}
      aria-hidden={!isNavVisible}
    >

      {navItems.map((item) => {
        const active = isActive(item.path);

        return (
          <button
            key={item.id}
            className={`bottom-nav-item ${
              active ? 'active' : ''
            }`}
            onClick={() =>
              handleNavClick(item)
            }
            type="button"
          >
            <span className="nav-icon">
              {active
                ? item.activeIcon
                : item.icon}
            </span>

            <span className="nav-label">
              {item.label}
            </span>

            {/* Badge for Cart, Orders, and Admin */}
            {item.badge !== undefined &&
              item.badge > 0 && (
                <span
                  className={`nav-badge ${
                    item.id === 'admin'
                      ? 'pending-orders'
                      : ''
                  }`}
                >
                  {item.badge > 99
                    ? '99+'
                    : item.badge}
                </span>
              )}
          </button>
        );
      })}

    </nav>
  );
};

export default BottomNav;